/**
 * The order of the Teams page (#2872): three orders, the "needs attention"
 * rule and the one-label filter. Pure functions, so the tiles and the table
 * share one rule. Every input is already on the page.
 */
import type { LastActiveTeam } from "../../../api/useAdminLastActive";
import type { AdminSetupStep } from "../../../api/useAdminSetup";
import type { AdminAllTeamsRow } from "../../../api/useAdminTeams";
import { calendarDaysBetween } from "./adminWords";
import { firstOpenStep, tileSetupWords, waitingDays } from "./setupWords";

export type TeamsSort = "priority" | "active" | "attention";

export const SETUP_WAIT_DAYS = 7;

export interface OrderContext {
  /** Setup steps by team id. A team with no entry has no setup rule. */
  setup: ReadonlyMap<string, readonly AdminSetupStep[]>;
  /** Last active counts by team id. */
  active: ReadonlyMap<string, LastActiveTeam>;
  /** The attention of each team, computed once per context. */
  attention: ReadonlyMap<string, Attention>;
}

/** One context for a list: the attention of every team is computed here, once. */
export function buildOrderContext(
  teams: readonly AdminAllTeamsRow[],
  setup: ReadonlyMap<string, readonly AdminSetupStep[]>,
  active: ReadonlyMap<string, LastActiveTeam>,
  now: Date,
): OrderContext {
  const attention = new Map<string, Attention>();
  for (const t of teams) attention.set(t.team_id, attentionOf(t, setup.get(t.team_id), now));
  return { setup, active, attention };
}

const NO_ATTENTION: Attention = {
  needs: false,
  telegram: false,
  telegramBand: false,
  unsent: 0,
  wait: 0,
  setupWaiting: false,
  reason: null,
};

export function attentionFor(ctx: OrderContext, row: AdminAllTeamsRow): Attention {
  return ctx.attention.get(row.team_id) ?? NO_ATTENTION;
}

/** True when the read gave `active_week` for the teams. An older API leaves it out. */
export function hasActiveWeek(active: ReadonlyMap<string, LastActiveTeam>): boolean {
  for (const v of active.values()) if (typeof v.active_week === "number") return true;
  return false;
}

export interface Attention {
  needs: boolean;
  /** A Telegram link fails. */
  telegram: boolean;
  /** Telegram trouble of either kind: a failing link or an unsent message. They sort first. */
  telegramBand: boolean;
  /** Messages not sent in 7 days. */
  unsent: number;
  /** Days the first open setup step waits, or 0. */
  wait: number;
  /** The setup wait reached the limit. */
  setupWaiting: boolean;
  /** The amber lead line, or null. */
  reason: string | null;
}

function cap(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function waitOf(
  row: AdminAllTeamsRow,
  steps: readonly AdminSetupStep[] | undefined,
  now: Date,
): number {
  if (!steps || !firstOpenStep(steps)) return 0;
  const fromStep = waitingDays(steps, now);
  if (fromStep !== null) return fromStep;
  const created = new Date(row.created_at);
  return Number.isNaN(created.getTime()) ? 0 : calendarDaysBetween(created, now);
}

/** A team needs attention when Telegram fails, a message was not sent in 7 days, or a setup step waits 7 days or more. */
export function attentionOf(
  row: AdminAllTeamsRow,
  steps: readonly AdminSetupStep[] | undefined,
  now: Date,
): Attention {
  const telegram = (row.adoption?.telegram_failing ?? 0) > 0;
  const unsent = row.adoption?.unsent_7d ?? 0;
  const wait = waitOf(row, steps, now);
  const setupWaiting = wait >= SETUP_WAIT_DAYS;
  const parts: string[] = [];
  if (telegram) parts.push("Telegram failing");
  if (unsent > 0) parts.push(`${unsent} not sent`);
  if (setupWaiting && steps) {
    const words = tileSetupWords(steps, now)
      .replace(/^Next: /, "")
      .replace(/, waiting .*$/, "");
    parts.push(`${cap(words)}, waiting ${wait} ${wait === 1 ? "day" : "days"}`);
  }
  return {
    needs: telegram || unsent > 0 || setupWaiting,
    telegram,
    telegramBand: telegram || unsent > 0,
    unsent,
    wait,
    setupWaiting,
    reason: parts.length > 0 ? parts.join(" · ") : null,
  };
}

export function byName(a: AdminAllTeamsRow, b: AdminAllTeamsRow): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

const PRIORITY_TITLE = { high: "High", normal: "Normal", low: "Low" } as const;

export function priorityOf(row: AdminAllTeamsRow): keyof typeof PRIORITY_TITLE {
  return row.admin_priority ?? "normal";
}

export function priorityWords(row: AdminAllTeamsRow): string {
  return PRIORITY_TITLE[priorityOf(row)];
}

export interface TeamGroup {
  key: string;
  /** The group head, or null for one plain list. */
  title: string | null;
  rows: AdminAllTeamsRow[];
}

type Compare = (a: AdminAllTeamsRow, b: AdminAllTeamsRow) => number;

/** Telegram problems first (failing link or unsent message), then the longest wait, then A to Z. */
function byAttention(ctx: OrderContext): Compare {
  return (a, b) => {
    const x = attentionFor(ctx, a);
    const y = attentionFor(ctx, b);
    if (x.telegramBand !== y.telegramBand) return x.telegramBand ? -1 : 1;
    if (x.wait !== y.wait) return y.wait - x.wait;
    return byName(a, b);
  };
}

/**
 * The rows in groups for one order. `inner` replaces the order inside each
 * group (a table header click). Empty groups are left out.
 */
export function groupTeams(
  rows: readonly AdminAllTeamsRow[],
  mode: TeamsSort,
  ctx: OrderContext,
  inner?: Compare,
): TeamGroup[] {
  if (mode === "active") {
    const count = (r: AdminAllTeamsRow) => ctx.active.get(r.team_id)?.active_week ?? 0;
    const sorted = [...rows].sort(inner ?? ((a, b) => count(b) - count(a) || byName(a, b)));
    return [{ key: "all", title: null, rows: sorted }];
  }
  if (mode === "priority") {
    const groups = (["high", "normal", "low"] as const).map((p) => ({
      key: p,
      title: PRIORITY_TITLE[p],
      rows: rows.filter((r) => priorityOf(r) === p).sort(inner ?? byName),
    }));
    return groups.filter((g) => g.rows.length > 0);
  }
  const flagged = rows.filter((r) => attentionFor(ctx, r).needs);
  const clear = rows.filter((r) => !flagged.includes(r));
  return [
    { key: "attention", title: "Needs attention", rows: flagged.sort(inner ?? byAttention(ctx)) },
    { key: "clear", title: "All clear", rows: clear.sort(inner ?? byName) },
  ].filter((g) => g.rows.length > 0);
}

export interface LabelOption {
  label: string;
  count: number;
}

/** The labels in use with their counts. Case is ignored. The first spelling shows. */
export function labelCounts(labels: readonly (string | null | undefined)[]): LabelOption[] {
  const seen = new Map<string, LabelOption>();
  for (const raw of labels) {
    const text = raw?.trim();
    if (!text) continue;
    const key = text.toLowerCase();
    const hit = seen.get(key);
    if (hit) hit.count += 1;
    else seen.set(key, { label: text, count: 1 });
  }
  return [...seen.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/** True when a row's label equals the chosen one, ignoring case. A null choice keeps every row. */
export function matchesLabel(label: string | null | undefined, chosen: string | null): boolean {
  if (chosen === null) return true;
  return (label ?? "").trim().toLowerCase() === chosen.trim().toLowerCase();
}
